import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const SecretariatMedical = () => {
  return (
    <>
      <Helmet>
        <title>Secrétariat Médical - ZiExter | Gestion d'agenda et prise de RDV patients</title>
        <meta name="description" content="Secrétariat médical externalisé pour médecins, dentistes, kinés. Prise de RDV, gestion d'agenda, rappels patients, permanence téléphonique médicale." />
        <link rel="canonical" href="https://ziexter.com/services/secretariat-medical" />
      </Helmet>

      <section className="page-hero bg-gradient-to-br from-secondary to-primary text-white">
        <div className="container-custom">
          <div className="max-w-3xl animate-fade-in-up">
            <nav className="flex items-center space-x-2 text-sm mb-4 text-gray-200">
              <Link to="/" className="hover:text-white">Accueil</Link>
              <span>/</span>
              <Link to="/services" className="hover:text-white">Services</Link>
              <span>/</span>
              <span className="text-white">Secrétariat Médical</span>
            </nav>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Secrétariat Médical – ZiExter
            </h1>
            <p className="text-xl text-gray-100 mb-4">
              Votre secrétariat médical externalisé, spécialisé et disponible
            </p>
            <p className="text-lg text-gray-200">
              Médecins, dentistes, kinés, ostéopathes, psychologues… ZiExter prend en charge votre gestion administrative et téléphonique pour que vous puissiez vous concentrer sur vos patients.
            </p>
          </div>
        </div>
      </section>

      <section className="py-8 bg-accent">
        <div className="container-custom">
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/devis" className="btn btn-secondary">Demandez un devis personnalisé</Link>
            <Link to="/contact" className="btn btn-outline-white">Contactez nos experts santé</Link>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto animate-fade-in-up">
            <h2 className="section-title text-center mb-8">Trop de patients à gérer… et pas assez de temps</h2>
            <p className="text-lg text-gray-700 mb-4">
              Téléphone qui sonne en pleine consultation, agenda surchargé, annulations de dernière minute, rappels à faire… Gérer un cabinet médical sans secrétaire, c'est perdre du temps précieux et risquer des rendez-vous manqués.
            </p>
            <p className="text-lg font-semibold text-primary">
              Avec ZiExter Secrétariat Médical, déléguez la gestion de votre agenda et de vos appels à une équipe formée aux spécificités du secteur médical. Professionnalisme, confidentialité et empathie garantis.
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Ce que comprend notre Secrétariat Médical</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: 'Prise de rendez-vous patients', desc: 'Nous gérons votre agenda médical en temps réel (Doctolib, Maiia, Mondocteur, Agenda Google, etc.).' },
              { title: 'Gestion des annulations et reports', desc: 'Nous optimisons votre planning en proposant les créneaux libérés immédiatement.' },
              { title: 'Rappels de rendez-vous automatiques', desc: 'SMS et appels de rappel avant consultation pour réduire les absences.' },
              { title: 'Permanence téléphonique médicale', desc: 'Accueil téléphonique professionnel, filtrage des urgences, prise de messages.' },
              { title: 'Gestion administrative', desc: 'Courriers patients, compte-rendus, envoi de documents (sur demande).' },
              { title: 'Respect de la confidentialité', desc: 'Formation RGPD et secret médical. Données sécurisées et conformes.' }
            ].map((item, i) => (
              <div key={i} className="card hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                <h3 className="font-bold mb-2">{item.title}</h3>
                <p className="text-sm text-gray-700">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Des professionnels de santé qui nous font confiance</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'Médecin généraliste, Marseille', problem: 'Appels non traités, patients frustrés.', solution: 'Secrétariat téléphonique avec prise de RDV sur Doctolib.', result: 'Taux de remplissage +30%, patients satisfaits.' },
              { title: 'Cabinet dentaire, Paris', problem: 'Nombreuses annulations non gérées.', solution: 'Rappels automatiques + re-planification immédiate.', result: 'Absences réduites de 40%.' },
              { title: 'Kinésithérapeute, Lyon', problem: 'Pas de secrétaire, image peu professionnelle.', solution: 'Permanence téléphonique et gestion d\'agenda complète.', result: 'Image valorisée, +25% de nouveaux patients.' }
            ].map((item, i) => (
              <div key={i} className="card hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 100}ms` }}>
                <h3 className="font-bold mb-3">{item.title}</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li><strong>Problème :</strong> {item.problem}</li>
                  <li><strong>Solution ZiExter :</strong> {item.solution}</li>
                  <li><strong>Résultat :</strong> {item.result}</li>
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Chiffres clés</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: '📅', stat: '3 000', label: 'RDV médicaux gérés par mois' },
              { icon: '⏱️', stat: '< 2 min', label: 'Temps moyen de prise de RDV' },
              { icon: '🔔', stat: '95%', label: 'Taux de rappel effectué' },
              { icon: '⭐', stat: '98%', label: 'Satisfaction patient' }
            ].map((item, i) => (
              <div key={i} className="card text-center hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="text-3xl mb-2">{item.icon}</div>
                <div className="text-2xl font-bold text-primary mb-1">{item.stat}</div>
                <p className="text-sm text-gray-600">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Avis de praticiens</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { quote: 'ZiExter gère mes RDV avec efficacité. Je peux enfin me concentrer sur mes consultations sans interruption.', name: 'Dr. Thomas Legrand', role: 'Médecin généraliste, Marseille' },
              { quote: 'Les rappels automatiques ont transformé la gestion de notre cabinet. Moins d\'absences, plus de revenus.', name: 'Dr. Claire Martin', role: 'Dentiste, Paris' },
              { quote: 'Un service pro, réactif et respectueux de la confidentialité. Je recommande vivement.', name: 'Antoine Rousseau', role: 'Kinésithérapeute, Lyon' }
            ].map((item, i) => (
              <div key={i} className="card hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 100}ms` }}>
                <p className="text-gray-700 mb-4 italic">"{item.quote}"</p>
                <div className="font-semibold">{item.name}</div>
                <div className="text-sm text-gray-600">{item.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">FAQ</h2>
          </div>
          <div className="max-w-4xl mx-auto space-y-4">
            {[
              { q: 'Mes données patients sont-elles sécurisées ?', a: 'Oui. Nos équipes sont formées au RGPD et au secret médical. Serveurs sécurisés, accès restreints.' },
              { q: 'Puis-je garder le contrôle de mon agenda ?', a: 'Absolument. Vous validez les créneaux disponibles et conservez la main sur les modifications.' },
              { q: 'Quel logiciel médical utilisez-vous ?', a: 'Nous nous intégrons à Doctolib, Maiia, Mondocteur, Calendly, Google Agenda, Outlook, etc.' },
              { q: 'Puis-je tester le service avant de m\'engager ?', a: 'Oui, période d\'essai de 15 jours pour tester notre efficacité.' },
              { q: 'Le service est-il disponible pour les spécialistes ?', a: 'Oui : dentistes, kinés, ostéos, psychologues, ophtalmologues, dermatologues, etc.' }
            ].map((item, i) => (
              <details key={i} className="card cursor-pointer">
                <summary className="font-semibold p-4 flex justify-between items-center">
                  <span>Q : {item.q}</span>
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="p-4 pt-0 text-gray-600">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gradient-to-r from-primary to-secondary text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center animate-fade-in-up">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Reprenez le contrôle de votre temps médical
            </h2>
            <p className="text-xl text-gray-100 mb-8">
              ZiExter devient votre secrétariat médical de confiance. Plus de temps pour vos patients, moins de stress administratif.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn btn-accent btn-lg">Demandez une démonstration</Link>
              <Link to="/devis" className="btn btn-outline-white btn-lg">Obtenez un devis personnalisé</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SecretariatMedical;
